// Bilingual (English / French) support for the portal. Docs and FAQ content are
// translated in their own modules; this file holds the language type, detection
// + persistence, and the UI-chrome string dictionary (nav, page heads, buttons).

export type Lang = "en" | "fr";

export const LANGS: Lang[] = ["en", "fr"];

export const LANG_LABEL: Record<Lang, string> = { en: "EN", fr: "FR" };

const STORAGE_KEY = "survantic_support_lang";

export function isLang(v: unknown): v is Lang {
  return v === "en" || v === "fr";
}

/** Saved preference → browser language → English. */
export function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLang(saved)) return saved;
  } catch {
    /* localStorage may be unavailable (private mode) — fall through */
  }
  const nav = (typeof navigator !== "undefined" && navigator.language) || "en";
  return nav.toLowerCase().startsWith("fr") ? "fr" : "en";
}

export function persistLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
}

// UI chrome strings. Keep keys identical across languages; the `UI[lang]` shape
// is the source of truth for the `t()` helper exposed by LangContext.
export interface UiStrings {
  brandSub: string;
  nav: { docs: string; faq: string; downloads: string };
  langSwitchLabel: string; // aria-label for the language toggle
  themeToggle: string; // title/aria for the theme button
  signIn: string;
  signOut: string;
  footerTagline: string;

  // Home
  homeTitle: string;
  homeSubDocsFaq: string; // "...find quick answers in the FAQ"
  homeSubDownloads: string; // ", or download the latest Survantic software"
  homeSignedInAs: string; // "Signed in as"
  homeSignInPrompt: string; // before the link
  homeSignInLink: string; // the link text
  homeSignInPromptAfter: string; // after the link
  cardDocsTitle: string;
  cardDocsDesc: string;
  cardDocsCta: string;
  cardFaqTitle: string;
  cardFaqDesc: string;
  cardFaqCta: string;
  cardDownloadsTitle: string;
  cardDownloadsDesc: string;
  cardDownloadsCta: string;

  // Docs
  docsTitle: string;
  docsSubtitle: string;
  docsSearchPlaceholder: string;
  docsSearchAria: string;
  docsIndexLead: string;
  docsArticlesCount: (n: number) => string;
  docsResultsCount: (n: number, query: string) => string;
  docsNoResults: (query: string) => string;
  docsBreadcrumbRoot: string;
  docsNotFound: { lead: string; link: string };
  metaWho: string;
  metaWhere: string;
  metaRequires: string;

  // FAQ
  faqTitle: string;
  faqSubtitleBefore: string; // "...see the "
  faqSubtitleLink: string; // "documentation"
  faqSubtitleAfter: string;

  // Login (dev/backend-enabled only)
  loginTitle: string;
  loginSubtitle: string;
  loginEmail: string;
  loginPassword: string;
  loginSubmit: string;
  loginSubmitting: string;
  loginNetworkError: string;
  loginFootBefore: string;
  loginFootLink: string;
  loginFootAfter: string;

  // Downloads (dev/backend-enabled only)
  dlTitle: string;
  dlSubtitle: (suffix: string) => string; // suffix may be "" or " for X deployments"
  dlSubtitleModeSuffix: (mode: string) => string;
  dlSignedInNotice: (email: string) => string; // before <strong>
  dlVerifyNotice: string;
  dlPlatformAll: string;
  dlLoadError: string;
  dlLoading: string;
  dlNone: (mode: string) => string;
  dlDownload: string;
  dlHideDetails: string;
  dlShowDetails: string;
  dlRequirements: string;
  dlWhatsNew: string;
  dlCategories: Record<string, string>;

  // Generic
  loading: string;
  pageNotFound: string;
  pageNotFoundBody: string; // before the <code> path
}

export const UI: Record<Lang, UiStrings> = {
  en: {
    brandSub: "Support",
    nav: { docs: "Documentation", faq: "FAQ", downloads: "Downloads" },
    langSwitchLabel: "Change language",
    themeToggle: "Toggle theme",
    signIn: "Sign in",
    signOut: "Sign out",
    footerTagline: "Support Portal",

    homeTitle: "How can we help?",
    homeSubDocsFaq: "Browse the documentation, find quick answers in the FAQ",
    homeSubDownloads: ", or download the latest Survantic software",
    homeSignedInAs: "Signed in as",
    homeSignInPrompt: "",
    homeSignInLink: "Sign in",
    homeSignInPromptAfter: " with your tenant credentials to access downloads.",
    cardDocsTitle: "Documentation",
    cardDocsDesc:
      "Task-oriented guides for operators and admins, with a fast search. Public — no sign-in needed.",
    cardDocsCta: "Browse docs →",
    cardFaqTitle: "FAQ",
    cardFaqDesc:
      "Quick answers to common questions — roles and access, cameras, recordings, alerts, and more.",
    cardFaqCta: "Read the FAQ →",
    cardDownloadsTitle: "Downloads",
    cardDownloadsDesc:
      "Installers, edge bundles, ISO images, and the mobile app — with versions and checksums.",
    cardDownloadsCta: "Get software →",

    docsTitle: "Documentation",
    docsSubtitle:
      "Task-oriented guides for operators and admins — what to do in the app.",
    docsSearchPlaceholder: "Search docs & FAQ…",
    docsSearchAria: "Search documentation",
    docsIndexLead:
      "Choose a guide, or search above. New to Survantic? Start with the Getting Started articles.",
    docsArticlesCount: (n) => `${n} article${n === 1 ? "" : "s"}`,
    docsResultsCount: (n, q) =>
      `${n} result${n === 1 ? "" : "s"} for “${q}”`,
    docsNoResults: (q) => `No results for “${q}”. Try different keywords.`,
    docsBreadcrumbRoot: "Docs",
    docsNotFound: {
      lead: "That page doesn’t exist. Pick a topic from the left or ",
      link: "start over",
    },
    metaWho: "Who",
    metaWhere: "Where",
    metaRequires: "Requires",

    faqTitle: "Frequently asked questions",
    faqSubtitleBefore:
      "Quick answers to common questions. For step-by-step detail, see the ",
    faqSubtitleLink: "documentation",
    faqSubtitleAfter: ".",

    loginTitle: "Sign in to Support",
    loginSubtitle:
      "Use the same email and password as your Survantic tenant. Any account under your organization can sign in.",
    loginEmail: "Email",
    loginPassword: "Password",
    loginSubmit: "Sign in",
    loginSubmitting: "Signing in…",
    loginNetworkError:
      "Couldn’t reach the server. Check your connection and try again.",
    loginFootBefore: "Manage your account and password in the ",
    loginFootLink: "Survantic app",
    loginFootAfter: ".",

    dlTitle: "Downloads",
    dlSubtitle: (suffix) => `Installers, edge bundles, and the mobile app${suffix}.`,
    dlSubtitleModeSuffix: (mode) => ` for ${mode} deployments`,
    dlSignedInNotice: (email) => `Signed in as ${email}. `,
    dlVerifyNotice:
      "Verify each file against its published SHA-256 checksum after downloading.",
    dlPlatformAll: "All platforms",
    dlLoadError: "Couldn’t load downloads. Check your connection and try again.",
    dlLoading: "Loading downloads…",
    dlNone: (mode) => `No downloads available for this platform on a ${mode} deployment.`,
    dlDownload: "Download",
    dlHideDetails: "Hide details",
    dlShowDetails: "Requirements & changelog",
    dlRequirements: "Requirements",
    dlWhatsNew: "What’s new",
    dlCategories: {
      edge: "Edge Devices",
      server: "On-Prem Server",
      mobile: "Mobile",
      tools: "Tools & Dependencies",
    },

    loading: "Loading…",
    pageNotFound: "Page not found",
    pageNotFoundBody: "We couldn’t find ",
  },
  fr: {
    brandSub: "Soutien",
    nav: { docs: "Documentation", faq: "FAQ", downloads: "Téléchargements" },
    langSwitchLabel: "Changer de langue",
    themeToggle: "Changer de thème",
    signIn: "Se connecter",
    signOut: "Se déconnecter",
    footerTagline: "Portail de soutien",

    homeTitle: "Comment pouvons-nous vous aider?",
    homeSubDocsFaq:
      "Parcourez la documentation, trouvez des réponses rapides dans la FAQ",
    homeSubDownloads: ", ou téléchargez la dernière version du logiciel Survantic",
    homeSignedInAs: "Connecté en tant que",
    homeSignInPrompt: "",
    homeSignInLink: "Connectez-vous",
    homeSignInPromptAfter:
      " avec les identifiants de votre organisation pour accéder aux téléchargements.",
    cardDocsTitle: "Documentation",
    cardDocsDesc:
      "Guides orientés tâches pour les opérateurs et les administrateurs, avec une recherche rapide. Public — aucune connexion requise.",
    cardDocsCta: "Consulter la documentation →",
    cardFaqTitle: "FAQ",
    cardFaqDesc:
      "Réponses rapides aux questions courantes — rôles et accès, caméras, enregistrements, alertes et plus encore.",
    cardFaqCta: "Lire la FAQ →",
    cardDownloadsTitle: "Téléchargements",
    cardDownloadsDesc:
      "Programmes d’installation, ensembles edge, images ISO et l’application mobile — avec versions et sommes de contrôle.",
    cardDownloadsCta: "Obtenir le logiciel →",

    docsTitle: "Documentation",
    docsSubtitle:
      "Guides orientés tâches pour les opérateurs et les administrateurs — quoi faire dans l’application.",
    docsSearchPlaceholder: "Rechercher dans la doc et la FAQ…",
    docsSearchAria: "Rechercher dans la documentation",
    docsIndexLead:
      "Choisissez un guide ou utilisez la recherche ci-dessus. Nouveau sur Survantic? Commencez par les articles de Prise en main.",
    docsArticlesCount: (n) => `${n} article${n === 1 ? "" : "s"}`,
    docsResultsCount: (n, q) =>
      `${n} résultat${n === 1 ? "" : "s"} pour « ${q} »`,
    docsNoResults: (q) =>
      `Aucun résultat pour « ${q} ». Essayez d’autres mots-clés.`,
    docsBreadcrumbRoot: "Documentation",
    docsNotFound: {
      lead: "Cette page n’existe pas. Choisissez un sujet à gauche ou ",
      link: "recommencez",
    },
    metaWho: "Qui",
    metaWhere: "Où",
    metaRequires: "Requiert",

    faqTitle: "Foire aux questions",
    faqSubtitleBefore:
      "Réponses rapides aux questions courantes. Pour le détail étape par étape, consultez la ",
    faqSubtitleLink: "documentation",
    faqSubtitleAfter: ".",

    loginTitle: "Connexion au soutien",
    loginSubtitle:
      "Utilisez le même courriel et mot de passe que votre organisation Survantic. N’importe quel compte de votre organisation peut se connecter.",
    loginEmail: "Courriel",
    loginPassword: "Mot de passe",
    loginSubmit: "Se connecter",
    loginSubmitting: "Connexion…",
    loginNetworkError:
      "Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.",
    loginFootBefore: "Gérez votre compte et votre mot de passe dans l’",
    loginFootLink: "application Survantic",
    loginFootAfter: ".",

    dlTitle: "Téléchargements",
    dlSubtitle: (suffix) =>
      `Programmes d’installation, ensembles edge et application mobile${suffix}.`,
    dlSubtitleModeSuffix: (mode) => ` pour les déploiements ${mode}`,
    dlSignedInNotice: (email) => `Connecté en tant que ${email}. `,
    dlVerifyNotice:
      "Vérifiez chaque fichier par rapport à sa somme de contrôle SHA-256 publiée après le téléchargement.",
    dlPlatformAll: "Toutes les plateformes",
    dlLoadError:
      "Impossible de charger les téléchargements. Vérifiez votre connexion et réessayez.",
    dlLoading: "Chargement des téléchargements…",
    dlNone: (mode) =>
      `Aucun téléchargement disponible pour cette plateforme sur un déploiement ${mode}.`,
    dlDownload: "Télécharger",
    dlHideDetails: "Masquer les détails",
    dlShowDetails: "Exigences et journal des modifications",
    dlRequirements: "Exigences",
    dlWhatsNew: "Nouveautés",
    dlCategories: {
      edge: "Appareils edge",
      server: "Serveur sur site",
      mobile: "Mobile",
      tools: "Outils et dépendances",
    },

    loading: "Chargement…",
    pageNotFound: "Page introuvable",
    pageNotFoundBody: "Impossible de trouver ",
  },
};
